import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useSound } from '@/hooks/useSound';

interface RematchRequest {
  id: string;
  original_room_id: string;
  initiator_player_id: string;
  initiator_name: string;
  status: string;
  new_room_id: string | null;
  created_at: string;
  expires_at: string;
}

interface RematchVote {
  id: string;
  request_id: string;
  player_id: string;
  player_name: string;
  vote: boolean;
  voted_at: string;
}

interface UseRematchRequestReturn {
  activeRequest: RematchRequest | null;
  votes: RematchVote[];
  hasVoted: boolean;
  initiateRematch: () => Promise<void>;
  castVote: (vote: boolean) => Promise<void>;
  timeRemaining: number;
}

export function useRematchRequest(
  roomId: string | null,
  playerId: string | null,
  playerName: string,
  onRematchAccepted: (newRoomId: string) => void
): UseRematchRequestReturn {
  const [activeRequest, setActiveRequest] = useState<RematchRequest | null>(null);
  const [votes, setVotes] = useState<RematchVote[]>([]);
  const [hasVoted, setHasVoted] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(60);
  const { toast } = useToast();
  const { playSound } = useSound();
  const lastRequestIdRef = useRef<string | null>(null);

  // Fetch active request for this room
  useEffect(() => {
    if (!roomId) return;

    const fetchActiveRequest = async () => {
      const { data, error } = await supabase
        .from('rematch_requests')
        .select('*')
        .eq('original_room_id', roomId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!error && data) {
        setActiveRequest(data as RematchRequest);
      }
    };

    fetchActiveRequest();

    // Subscribe to rematch requests
    const requestChannel = supabase
      .channel(`rematch-requests-${roomId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'rematch_requests',
          filter: `original_room_id=eq.${roomId}`,
        },
        (payload) => {
          console.log('Rematch request update:', payload);
          if (payload.eventType === 'INSERT') {
            const newRequest = payload.new as RematchRequest;
            setActiveRequest(newRequest);
            setVotes([]);
            setHasVoted(false);
            
            // Play notification sound for new rematch request (if not initiated by self)
            if (newRequest.initiator_player_id !== playerId && lastRequestIdRef.current !== newRequest.id) {
              playSound('rematch', 0.5);
              lastRequestIdRef.current = newRequest.id;
            }
          } else if (payload.eventType === 'UPDATE') {
            const updated = payload.new as RematchRequest;
            setActiveRequest(updated);
            
            if (updated.status === 'accepted' && updated.new_room_id) {
              onRematchAccepted(updated.new_room_id);
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(requestChannel);
    };
  }, [roomId, onRematchAccepted]);

  // Subscribe to votes for active request
  useEffect(() => {
    if (!activeRequest) return;

    const fetchVotes = async () => {
      const { data, error } = await supabase
        .from('rematch_votes')
        .select('*')
        .eq('request_id', activeRequest.id);

      if (!error && data) {
        setVotes(data as RematchVote[]);
        const myVote = data.find(v => v.player_id === playerId);
        setHasVoted(!!myVote);
      }
    };

    fetchVotes();

    const voteChannel = supabase
      .channel(`rematch-votes-${activeRequest.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'rematch_votes',
          filter: `request_id=eq.${activeRequest.id}`,
        },
        (payload) => {
          console.log('New vote:', payload);
          setVotes(prev => [...prev, payload.new as RematchVote]);
          
          if ((payload.new as RematchVote).player_id === playerId) {
            setHasVoted(true);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(voteChannel);
    };
  }, [activeRequest?.id, playerId]);

  // Countdown timer
  useEffect(() => {
    if (!activeRequest || activeRequest.status !== 'pending') {
      setTimeRemaining(60);
      return;
    }

    const expiresAt = new Date(activeRequest.expires_at).getTime();
    
    const updateTimer = () => {
      const now = Date.now();
      const remaining = Math.max(0, Math.floor((expiresAt - now) / 1000));
      setTimeRemaining(remaining);
      
      if (remaining === 0) {
        setActiveRequest(prev => prev ? { ...prev, status: 'expired' } : null);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [activeRequest]);

  const initiateRematch = useCallback(async () => {
    if (!roomId || !playerId) return;

    try {
      const { data, error } = await supabase
        .from('rematch_requests')
        .insert({
          original_room_id: roomId,
          initiator_player_id: playerId,
          initiator_name: playerName,
        })
        .select()
        .single();

      if (error) throw error;

      setActiveRequest(data as RematchRequest);
      
      // Auto-vote yes for initiator
      await supabase
        .from('rematch_votes')
        .insert({
          request_id: data.id,
          player_id: playerId,
          player_name: playerName,
          vote: true,
        });

      setHasVoted(true);
      
      toast({
        title: "Rematch requested!",
        description: "Waiting for other players to respond...",
      });
    } catch (error) {
      console.error('Error initiating rematch:', error);
      toast({
        title: "Error",
        description: "Failed to start rematch request",
        variant: "destructive",
      });
    }
  }, [roomId, playerId, playerName, toast]);

  const castVote = useCallback(async (vote: boolean) => {
    if (!activeRequest || !playerId || hasVoted) return;

    try {
      const { error } = await supabase
        .from('rematch_votes')
        .insert({
          request_id: activeRequest.id,
          player_id: playerId,
          player_name: playerName,
          vote,
        });

      if (error) throw error;

      setHasVoted(true);

      // Trigger processing and check response for immediate navigation
      const { data: response, error: processError } = await supabase.functions.invoke('process-rematch', {
        body: { requestId: activeRequest.id, action: 'check_and_process' },
      });

      console.log('Process rematch response:', response);

      // If rematch was accepted, trigger navigation immediately (don't wait for realtime)
      if (!processError && response?.status === 'accepted' && response?.newRoomId) {
        console.log('Rematch accepted immediately, navigating to:', response.newRoomId);
        onRematchAccepted(response.newRoomId);
      }
    } catch (error) {
      console.error('Error casting vote:', error);
      toast({
        title: "Error",
        description: "Failed to submit your vote",
        variant: "destructive",
      });
    }
  }, [activeRequest, playerId, playerName, hasVoted, toast, onRematchAccepted]);

  return {
    activeRequest,
    votes,
    hasVoted,
    initiateRematch,
    castVote,
    timeRemaining,
  };
}
